import React, { useEffect, useState } from 'react';
import { Button, Icon, Loader, Popup } from 'semantic-ui-react';
import { fetchGroups } from '../../services/Groups';
import { Group } from '../../types/Group';
import sendToast from '../../../utils/Toast';
import { normalizeId } from '../../../utils/normalizeId';

interface Props {
    facilityId: string;
    residentId: string;
    selectedGroupId: string | null;
    onSelectGroup: (group: Group | null) => void;
}

const PAGE_SIZE = 20;

// listDetailedGroups returns the id arrays; the populated Residents/Staff arrays
// are not guaranteed, so read those only as a fallback.
const memberCount = (group: Group) =>
    (group.ResidentIds?.length ?? group.Residents?.length ?? 0)
    + (group.StaffIds?.length ?? group.Staff?.length ?? 0);

const GroupsPanel = ({ facilityId, residentId, selectedGroupId, onSelectGroup }: Props) => {
    const [groups, setGroups] = useState<Group[]>([]);
    // True until the first fetch settles. The effect that starts it runs after
    // paint, so beginning false leaves the card empty until the groups appear.
    const [loading, setLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [imageLoadError, setImageLoadError] = useState<Record<string, boolean>>({});

    useEffect(() => {
        setPageNo(1);
        fetchGroupsList(1, true);
    }, [facilityId, residentId]);

    const fetchGroupsList = async (page: number, reset = false) => {
        try {
            if (reset) setHasMore(true);
            setLoading(true);
            const filter = {
                Facility: facilityId,
                ResidentId: String(residentId),
            };
            const response = await fetchGroups(filter, page, PAGE_SIZE);
            if (response.TotRecords <= page * PAGE_SIZE) {
                setHasMore(false);
            }
            const groupsWithImages: Group[] = response.Result || [];
            if (reset) {
                setGroups(groupsWithImages);
            } else {
                setGroups(prev => [...prev, ...groupsWithImages]);
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error fetching groups');
        } finally {
            setLoading(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = pageNo + 1;
        setPageNo(nextPage);
        fetchGroupsList(nextPage);
    };

    const handleGroupClick = (group: Group) => {
        const groupId = normalizeId(group._id);
        if (selectedGroupId && selectedGroupId === groupId) {
            onSelectGroup(null); // deselect
        } else {
            onSelectGroup(group);
        }
    };

    return (
        <div className="groups-panel">
            <div className="groups-panel-header section-header">
                <Icon name="users" />
                <span>Groups</span>
            </div>
            <div className="groups-panel-list">
                {loading && groups.length === 0 && (
                    <div className="groups-panel-loader">
                        <Loader active inline="centered" size="small" />
                    </div>
                )}
                {groups.length === 0 && !loading && (
                    <p className="groups-empty">No groups found</p>
                )}
                {groups.map((group, index) => {
                    const groupId = normalizeId(group._id);
                    const groupKey = groupId || `${group.Name || 'group'}-${index}`;
                    const isSelected = Boolean(selectedGroupId && selectedGroupId === groupId);
                    return (
                        <div key={groupKey} className={`group-item ${isSelected ? 'selected' : ''}`}>
                            <Popup
                                content={isSelected ? 'Remove filter' : 'Filter by this group'}
                                position="bottom center"
                                size="mini"
                                inverted
                                mouseEnterDelay={400}
                                trigger={
                                    <div
                                        className="group-item-left"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleGroupClick(group)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleGroupClick(group);
                                            }
                                        }}
                                    >
                                        {group.ImageUrl && !imageLoadError[groupKey] ? (
                                            <img
                                                src={group.ImageUrl}
                                                alt=""
                                                className="group-item-image"
                                                onError={() => setImageLoadError(prev => ({ ...prev, [groupKey]: true }))}
                                            />
                                        ) : (
                                            <div className="group-item-image group-item-image-fallback">
                                                <Icon name="users" />
                                            </div>
                                        )}
                                        <div className="group-item-text">
                                            <span className="group-item-name">{group.Name}</span>
                                            {group.GroupCategory && <span className="group-item-category">{group.GroupCategory}</span>}
                                        </div>
                                        {memberCount(group) > 0 && (
                                            <span className="group-count">{memberCount(group)}</span>
                                        )}
                                    </div>
                                }
                            />
                        </div>
                    );
                })}
                {hasMore && groups.length > 0 && (
                    <div className="load-more-button">
                        <Button onClick={handleLoadMore} loading={loading} disabled={loading} size="small">
                            Load More
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupsPanel;
